/**
 * @fileoverview System-level inspection template grid and detail slideout page.
 * @module system-inspection-templates
 *
 * Responsibilities:
 * - Render the system inspection template grid and selected template detail panel.
 * - Read and mutate system inspection template data through Dexie-backed local-first services.
 * - Provide template creation, publishing, archiving, and deletion actions.
 *
 * Dependencies:
 * - react, react-router-dom, lucide-react
 * - ../services/systemInspectionTemplateService
 * - ../services/aiFoundryService
 * - @ui-admin/components (GridPageShell, DataGrid, InlineEditableField, AuditLogTimeline)
 *
 * Architecture:
 * - Local-first PWA page: Dexie is the UI source of truth and API clients are not called directly.
 * - All data access goes through systemInspectionTemplateService which handles Dexie operations
 *   and background sync via wakeLocalWrite().
 *
 * Notes:
 * - This page manages system-wide templates that can be published to the template library
 *   for adoption by tenant departments.
 */
import { useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { liveQuery } from 'dexie';
import {
  Plus, Copy, Archive, Trash2, Pencil, Trees, Settings, FileText, X, Maximize2, ChevronRight, ChevronDown,
  CheckCircle2, XCircle, Clock, Send, Wifi, WifiOff, PenTool, Users,
  Globe, Lock, Sparkles,
  type LucideIcon,
} from 'lucide-react';
import { GridPageShell } from '@ui-admin/components/GridPageShell';
import type { DataGridColumn, DataGridFilter, DataGridRowAction } from '@ui-admin/components/DataGrid';
import { InlineEditableField, TemplateDetailSlideoutPanel as SharedTemplateDetailSlideoutPanel } from '@ui-admin';
import { useTheme, type GridTheme } from '@ui-admin/contexts/ThemeContext';
import AuditLogTimeline from '@ui-admin/components/AuditLogTimeline';
import type { TemplateBuilderTemplate, TemplateType } from '@ui-admin';
import { TEMPLATE_TYPE_LABELS } from '@ui-admin';
import {
  listSystemInspectionTemplates,
  listLocalSystemInspectionTemplates,
  getSystemCanvasTemplate,
  createSystemCanvasTemplate,
  updateSystemCanvasTemplate,
  publishSystemInspectionTemplate,
  archiveSystemInspectionTemplate,
  deleteSystemInspectionTemplate,
  createSystemBlankTemplate,
  createSystemTemplateFromAIMetadata,
  installQaReferenceTemplate,
  type SystemInspectionTemplateListItem,
} from '../services/systemInspectionTemplateService';
import ConfirmDeleteModal from '../components/ui/ConfirmDeleteModal';
import { SquirrelInterviewModal } from '@firesquirrel/ui-admin';
import type { ExtractedMetadata } from '@firesquirrel/ui-admin';
import { squirrelMessageSender, parseDocumentForTemplate } from '../services/aiFoundryService';
import TabPanel from '../components/ui/TabPanel';
import StatusBadge from '../components/ui/StatusBadge';
import { fetchAuditLogs } from '../services/systemAuditLogService';

/**
 * @component NewTemplateMenu
 *
 * Purpose:
 * - Render split creation actions for manual and Squirrel-assisted template creation.
 *
 * Props:
 * - onManual: Opens the manual local-first creation modal.
 * - onSquirrel: Opens the AI-assisted creation flow.
 *
 * Behavior:
 * - Keeps dropdown state local to the component and closes on outside click.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - Must not call APIs or persistence services directly.
 *
 * Performance:
 * - Small presentational control with no expensive derived state.
 */
function NewTemplateMenu({
  onManual,
  onSquirrel,
  onQaReference,
}: {
  onManual: () => void;
  onSquirrel: () => void;
  onQaReference: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative flex">
      <button
        onClick={onManual}
        className="inline-flex items-center gap-2 pl-4 pr-3 py-2.5 text-white rounded-l-lg text-sm font-medium transition-colors bg-orange-600 hover:bg-orange-700"
      >
        <Plus className="h-4 w-4" /> New Template
      </button>
      <div className="w-px bg-white/30" />
      <button
        onClick={() => setOpen((p) => !p)}
        title="More creation options"
        className="inline-flex items-center px-2 py-2.5 text-white rounded-r-lg text-sm transition-colors bg-orange-600 hover:bg-orange-700"
      >
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1.5 z-50 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden min-w-[220px]">
          <button
            onClick={() => { setOpen(false); onManual(); }}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
          >
            <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
              <Plus className="h-4 w-4 text-gray-600" />
            </div>
            <div>
              <div className="font-medium">Manual</div>
              <div className="text-[11px] text-gray-400 mt-0.5">Start from a blank template</div>
            </div>
          </button>
          <div className="h-px bg-gray-100 mx-3" />
          <button
            onClick={() => { setOpen(false); onSquirrel(); }}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-amber-50 transition-colors text-left"
          >
            <div className="w-7 h-7 rounded-lg overflow-hidden shrink-0 border border-amber-200">
              <img src="/icons/squirrel-helper.png" alt="Squirrel" className="w-full h-full object-cover" />
            </div>
            <div>
              <div className="font-medium flex items-center gap-1.5">
                Let Squirrel Help
                <Sparkles className="h-3 w-3 text-amber-500" />
              </div>
              <div className="text-[11px] text-gray-400 mt-0.5">AI-guided template setup</div>
            </div>
          </button>
          <div className="h-px bg-gray-100 mx-3" />
          <button
            onClick={() => { setOpen(false); onQaReference(); }}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 transition-colors text-left"
          >
            <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
              <Plus className="h-4 w-4 text-blue-700" />
            </div>
            <div>
              <div className="font-medium">Install QA Reference Template</div>
              <div className="text-[11px] text-gray-400 mt-0.5">Adds a fresh copy to the library — all 18 widgets + modules</div>
            </div>
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TYPE_ICONS: Record<string, LucideIcon> = {
  wildland: Trees,
  custom: Settings,
};

const TYPE_COLORS: Record<string, string> = {
  wildland: 'bg-green-100 text-green-700',
  custom: 'bg-gray-100 text-gray-700',
};

const STATUS_DOT: Record<string, string> = {
  draft: 'bg-amber-400',
  published: 'bg-green-500',
  archived: 'bg-gray-400',
};

const STATUS_BADGE_VARIANT: Record<string, 'success' | 'warning' | 'neutral'> = {
  draft: 'warning',
  published: 'success',
  archived: 'neutral',
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;
  return new Date(dateStr).toLocaleDateString();
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// Grid Columns
// ---------------------------------------------------------------------------

const columns: DataGridColumn<SystemInspectionTemplateListItem>[] = [
  {
    id: 'name',
    header: 'Template Name',
    accessorKey: 'name',
    sortable: true,
    filterable: true,
    minWidth: 220,
    cell: (info) => {
      const t = info.row.original;
      const FallbackIcon = TYPE_ICONS[t.templateType] ?? FileText;
      const colorCls = TYPE_COLORS[t.templateType] ?? 'bg-gray-100 text-gray-700';
      return (
        <div className="flex items-center gap-2.5 min-w-0">
          <div className={`shrink-0 w-7 h-7 rounded-md flex items-center justify-center ${colorCls}`}>
            <FallbackIcon className="h-3.5 w-3.5" />
          </div>
          <div className="min-w-0">
            <span className="text-sm font-medium text-gray-900 truncate block">{t.name}</span>
            {t.description && (
              <div className="text-xs text-gray-400 truncate max-w-[280px]">{t.description}</div>
            )}
          </div>
        </div>
      );
    },
  },
  {
    id: 'templateType',
    header: 'Type',
    accessorFn: (row) => TEMPLATE_TYPE_LABELS[row.templateType as TemplateType] ?? row.templateType,
    sortable: true,
    filterable: true,
    width: 130,
    cell: (info) => {
      const t = info.row.original;
      const colorCls = TYPE_COLORS[t.templateType] ?? 'bg-gray-100 text-gray-700';
      return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colorCls}`}>
          {TEMPLATE_TYPE_LABELS[t.templateType as TemplateType] ?? t.templateType}
        </span>
      );
    },
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    sortable: true,
    filterable: true,
    width: 110,
    cell: (info) => {
      const t = info.row.original;
      const label = t.status.charAt(0).toUpperCase() + t.status.slice(1);
      return <StatusBadge label={label} variant={STATUS_BADGE_VARIANT[t.status] ?? 'neutral'} />;
    },
  },
  {
    id: 'library',
    header: 'Library',
    accessorFn: (row) => row.librarySettings?.isInLibrary ? 'In Library' : 'Private',
    sortable: true,
    width: 120,
    cell: (info) => {
      const t = info.row.original;
      return t.librarySettings?.isInLibrary ? (
        <span className="inline-flex items-center gap-1 text-xs text-green-700">
          <Globe className="h-3.5 w-3.5" /> In Library
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-xs text-gray-400">
          <Lock className="h-3.5 w-3.5" /> Private
        </span>
      );
    },
  },
  {
    id: 'version',
    header: 'Version',
    accessorKey: 'version',
    sortable: true,
    width: 80,
    cell: (info) => (
      <span className="text-xs text-gray-500">v{info.row.original.version}</span>
    ),
  },
  {
    id: 'updatedAt',
    header: 'Updated',
    accessorKey: 'updatedAt',
    sortable: true,
    width: 130,
    cell: (info) => (
      <span className="text-xs text-gray-500">{timeAgo(info.row.original.updatedAt)}</span>
    ),
  },
];

// ---------------------------------------------------------------------------
// Filter Chips
// ---------------------------------------------------------------------------

const filterChips: DataGridFilter[] = [
  {
    id: 'status',
    label: 'Status',
    type: 'select',
    options: [
      { value: 'draft', label: 'Draft', color: 'amber' },
      { value: 'published', label: 'Published', color: 'green' },
      { value: 'archived', label: 'Archived', color: 'gray' },
    ],
  },
  {
    id: 'templateType',
    label: 'Type',
    type: 'select',
    options: [
      { value: 'Wildland', label: 'Wildland' },
      { value: 'Custom', label: 'Custom' },
    ],
  },
];

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

/**
 * @component SystemInspectionTemplatesPage
 *
 * Purpose:
 * - Compose the local-first system inspection template grid, detail panel, and creation flows.
 *
 * Props:
 * - None.
 *
 * Behavior:
 * - Subscribes to Dexie-backed template data with liveQuery and routes all mutations through services.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - No direct API CRUD calls; reads/writes must go through systemInspectionTemplateService.
 *
 * Performance:
 * - Memoizes static grid columns and row actions; uses liveQuery for reactive grid updates.
 */
export default function SystemInspectionTemplatesPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [templates, setTemplates] = useState<SystemInspectionTemplateListItem[]>([]);
  const [filteredCount, setFilteredCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState<SystemInspectionTemplateListItem | null>(null);
  const [fullTemplate, setFullTemplate] = useState<TemplateBuilderTemplate | null>(null);
  const [loadingFull, setLoadingFull] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SystemInspectionTemplateListItem | null>(null);
  const [squirrelModalOpen, setSquirrelModalOpen] = useState(false);
  const [formNames, setFormNames] = useState<Map<string, string>>(new Map());

  // LiveQuery for reactive template list updates
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const subscription = liveQuery(async () => {
      return await listLocalSystemInspectionTemplates();
    }).subscribe({
      next: (data) => {
        if (cancelled) return;
        setTemplates(data);
        setLoading(false);
      },
      error: (error) => {
        console.warn('[SystemInspectionTemplatesPage] template watcher error:', error);
        if (cancelled) setLoading(false);
      },
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  // Load full TemplateBuilderTemplate when selection changes
  useEffect(() => {
    if (!selectedItem) { setFullTemplate(null); return; }
    let cancelled = false;
    setLoadingFull(true);
    setFullTemplate(null);
    getSystemCanvasTemplate(selectedItem._id)
      .then((t) => { if (!cancelled) { setFullTemplate(t); setLoadingFull(false); } })
      .catch(() => { if (!cancelled) setLoadingFull(false); });
    return () => { cancelled = true; };
  }, [selectedItem?._id]);

  // Load form names when fullTemplate changes
  useEffect(() => {
    if (!fullTemplate?.formRefs?.length) { setFormNames(new Map()); return; }
    let cancelled = false;
    const formIds = fullTemplate.formRefs.map(r => r.formId);
    Promise.allSettled(formIds.map(id => import('../services/systemFormService').then(m => m.getFormById(id))))
      .then(results => {
        if (cancelled) return;
        const names = new Map<string, string>();
        results.forEach((r, idx) => {
          if (r.status === 'fulfilled') {
            if (r.value) {
              names.set(r.value._id, r.value.name);
            } else {
              names.set(formIds[idx], '⚠️ Form not found');
            }
          } else {
            names.set(formIds[idx], '⚠️ Error loading form');
          }
        });
        setFormNames(names);
      });
    return () => { cancelled = true; };
  }, [fullTemplate]);

  const refreshSelected = useCallback(async (id: string) => {
    try {
      const fresh = await listSystemInspectionTemplates();
      setTemplates(fresh);
      const found = fresh.find((t) => t._id === id);
      if (found) setSelectedItem(found);
      const full = await getSystemCanvasTemplate(id);
      if (full) setFullTemplate(full);
    } catch { /* ignore */ }
  }, []);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [creatingTemplate, setCreatingTemplate] = useState(false);

  const handleCreate = useCallback(() => {
    setCreateModalOpen(true);
    setNewTemplateName('');
  }, []);

  const handleCreateConfirm = useCallback(async () => {
    if (!newTemplateName.trim()) return;
    setCreatingTemplate(true);
    try {
      const created = await createSystemBlankTemplate(newTemplateName.trim(), 'wildland');
      setCreateModalOpen(false);
      setNewTemplateName('');
      navigate(`/inspection-templates/${created._id}`);
    } catch {
      /* error handled silently */
    } finally {
      setCreatingTemplate(false);
    }
  }, [newTemplateName, navigate]);

  const handleSquirrelCreate = useCallback(() => {
    setSquirrelModalOpen(true);
  }, []);

  /**
   * @process handleInstallQaReference
   * Steps:
   * 1. Persist a fresh copy of the QA Reference bundle into Dexie
   *    (system_inspection_templates + system_forms with new ids).
   * 2. Navigate to the editor for the newly-installed template.
   *
   * Failure Handling: surface a console error + alert; the menu stays
   * usable so the inspector can retry.
   * Idempotency: NOT REQUIRED — each click installs a new copy.
   */
  const handleInstallQaReference = useCallback(async () => {
    try {
      const { templateId } = await installQaReferenceTemplate();
      navigate(`/inspection-templates/${templateId}`);
    } catch (err) {
      console.error('[SystemInspectionTemplatesPage] QA Reference install failed:', err);
      alert('Failed to install QA Reference Template. See console for details.');
    }
  }, [navigate]);

  const handleSquirrelComplete = useCallback(async (metadata: ExtractedMetadata) => {
    try {
      const created = await createSystemTemplateFromAIMetadata(metadata);

      const generatedForm = (metadata as any).generatedForm;
      if (generatedForm?.formName) {
        try {
          const { createForm } = await import('../services/systemFormService');
          const formFields: any[] = [];
          const layoutSections: any[] = [];
          let fieldCounter = 0;
          for (const [sIdx, section] of (generatedForm.sections ?? []).entries()) {
            const sectionId = `sec-${Date.now()}-${(++fieldCounter).toString(36)}-${sIdx}`;
            const sectionFieldIds: string[] = [];
            for (const f of (section.fields ?? [])) {
              const fieldId = `field-${Date.now()}-${(++fieldCounter).toString(36)}`;
              const field: any = {
                id: fieldId,
                label: f.label,
                type: f.widgetId ? 'dataBound' : (f.type ?? 'text'),
                required: f.required ?? false,
                extras: f.widgetId
                  ? { widgetId: f.widgetId, dataSource: f.dataSource }
                  : {},
              };
              formFields.push(field);
              sectionFieldIds.push(fieldId);
            }
            layoutSections.push({
              id: sectionId,
              title: section.title ?? `Section ${sIdx + 1}`,
              columns: 1,
              order: sIdx,
              fieldIds: sectionFieldIds,
            });
          }
          const form = await createForm({
            name: generatedForm.formName,
            description: generatedForm.formDescription ?? '',
            fields: formFields,
            layout: { sections: layoutSections },
          });
          await updateSystemCanvasTemplate(created._id, {
            formRefs: [{ formId: form._id, label: form.name, order: 0 }],
          } as any);
        } catch (formErr) {
          console.warn('[handleSquirrelComplete] form creation failed — template created without form:', formErr);
        }
      }

      setSquirrelModalOpen(false);
      navigate(`/inspection-templates/${created._id}`);
    } catch {
      alert('Failed to create template. Please try again.');
    }
  }, [navigate]);

  const handleDuplicate = useCallback(async (id: string) => {
    try {
      const source = await getSystemCanvasTemplate(id);
      if (!source) return;
      const dup = await createSystemCanvasTemplate({
        name: `${source.name} (Copy)`,
        templateType: source.templateType,
        description: source.description,
      });
      await updateSystemCanvasTemplate(dup._id, {
        parameters: source.parameters,
        formRefs: source.formRefs,
        rules: source.rules,
      });
    } catch { /* ignore */ }
  }, []);

  const handlePublish = useCallback(async (id: string) => {
    try {
      await publishSystemInspectionTemplate(id);
      // Note: liveQuery will automatically update the templates list when Dexie changes
      if (selectedItem?._id === id) {
        await refreshSelected(id);
      }
    } catch (err) {
      console.error('[SystemInspectionTemplatesPage] publish failed:', err);
    }
  }, [selectedItem, refreshSelected]);

  const handleArchive = useCallback(async (id: string) => {
    try {
      await archiveSystemInspectionTemplate(id);
      // Note: liveQuery will automatically update the templates list when Dexie changes
      if (selectedItem?._id === id) {
        await refreshSelected(id);
      }
    } catch { /* ignore */ }
  }, [selectedItem, refreshSelected]);

  const handleDelete = useCallback((id: string) => {
    const item = templates.find((t) => t._id === id);
    if (item) setDeleteTarget(item);
  }, [templates]);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await deleteSystemInspectionTemplate(deleteTarget._id);
    } catch { /* ignore */ }
    if (selectedItem?._id === deleteTarget._id) {
      setSelectedItem(null);
      setFullTemplate(null);
    }
    setDeleteTarget(null);
  }, [deleteTarget, selectedItem]);

  const handleRowClick = useCallback((row: SystemInspectionTemplateListItem) => {
    setSelectedItem(row);
  }, []);

  const getRowId = useCallback((row: SystemInspectionTemplateListItem) => row._id, []);

  const rowActions = useMemo<DataGridRowAction<SystemInspectionTemplateListItem>[]>(() => [
    {
      id: 'edit',
      label: 'Edit',
      icon: Pencil,
      onClick: (row) => navigate(`/inspection-templates/${row._id}`),
    },
    {
      id: 'duplicate',
      label: 'Duplicate',
      icon: Copy,
      onClick: (row) => handleDuplicate(row._id),
    },
    {
      id: 'publish',
      label: 'Publish',
      icon: Send,
      onClick: (row) => handlePublish(row._id),
      hidden: (row) => row.status !== 'draft',
    },
    {
      id: 'archive',
      label: 'Archive',
      icon: Archive,
      onClick: (row) => handleArchive(row._id),
      hidden: (row) => row.status !== 'published',
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: Trash2,
      onClick: (row) => handleDelete(row._id),
      variant: 'danger',
    },
  ], [navigate, handleDuplicate, handlePublish, handleArchive, handleDelete]);

  const gridColumns = useMemo(() => columns, []);
  const panelTemplate = selectedItem
    ? (fullTemplate ?? ({
      ...selectedItem,
      templateTypeName: TEMPLATE_TYPE_LABELS[selectedItem.templateType as TemplateType] ?? selectedItem.templateType,
      formRefs: [],
      rules: [],
      parameters: {
        autoCreateReinspectionWhenViolations: false,
        selfCertificationEnabled: false,
        emailOwnerOnComplete: false,
        emailInspectorSummary: false,
        requireInspectorSignature: false,
        requireResponsiblePartySignature: false,
        allowOfflineMode: true,
      },
      createdBy: 'system-user',
      updatedBy: 'system-user',
    } as unknown as TemplateBuilderTemplate))
    : null;
  const librarySlot = selectedItem ? (
    <div className="border-b border-gray-100">
      <div className="w-full px-4 py-3 flex items-center justify-between text-left">
        <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Library Settings</span>
      </div>
      <div className="px-4 pb-4 space-y-2 text-xs">
        <div className="flex items-center justify-between">
          <span className="font-medium text-gray-500">In Library</span>
          {selectedItem.librarySettings?.isInLibrary ? (
            <span className="inline-flex items-center gap-1 text-green-700">
              <Globe className="h-3.5 w-3.5" /> Public
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-gray-400">
              <Lock className="h-3.5 w-3.5" /> Private
            </span>
          )}
        </div>
        <div className="flex items-center justify-between">
          <span className="font-medium text-gray-500">Visibility</span>
          <span className="text-gray-700 capitalize">{selectedItem.librarySettings?.visibility ?? 'private'}</span>
        </div>
        {(selectedItem.librarySettings?.tags ?? []).length > 0 && (
          <div>
            <span className="font-medium text-gray-500 block mb-1">Tags</span>
            <div className="flex flex-wrap gap-1">
              {(selectedItem.librarySettings?.tags ?? []).map((tag) => (
                <span key={tag} className="px-1.5 py-0.5 rounded bg-orange-50 text-orange-700 text-[10px] font-medium">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  ) : null;

  const panelContent = selectedItem && panelTemplate ? (
    <SharedTemplateDetailSlideoutPanel
      template={panelTemplate}
      loadingDetails={loadingFull}
      formNames={formNames}
      librarySlot={librarySlot}
      onEdit={() => navigate(`/inspection-templates/${selectedItem._id}`)}
      onPublish={handlePublish}
      onDuplicate={handleDuplicate}
      onArchive={handleArchive}
      onDelete={handleDelete}
      onDescriptionSave={async (description) => {
        await updateSystemCanvasTemplate(selectedItem._id, { description: description || undefined });
        await refreshSelected(selectedItem._id);
      }}
      auditSlot={
        <AuditLogTimeline
          collection="systemInspectionTemplates"
          documentId={selectedItem._id}
          theme={theme}
          active
          fetchFn={fetchAuditLogs}
        />
      }
      theme={theme}
    />
  ) : undefined;

  if (loading) return null;

  return (
    <>
      <GridPageShell<SystemInspectionTemplateListItem>
        gridId="system-inspection-templates"
        gridType="system-inspection-templates"
        title="Inspection Templates"
        subtitle={`${filteredCount !== null ? filteredCount : templates.length} template${(filteredCount !== null ? filteredCount : templates.length) !== 1 ? 's' : ''}`}
        onFilteredCountChange={setFilteredCount}
        columns={gridColumns}
        data={templates}
        getRowId={getRowId}
        rowActions={rowActions}
        filters={filterChips}
        onRowClick={handleRowClick}
        activeRowId={selectedItem?._id ?? null}
        searchPlaceholder="Search templates..."
        toolbarActions={
          <NewTemplateMenu
            onManual={handleCreate}
            onSquirrel={handleSquirrelCreate}
            onQaReference={handleInstallQaReference}
          />
        }
        exportable
        emptyIcon={FileText}
        emptyTitle="No templates yet"
        emptySubtitle="Create your first system inspection template to define the forms and parameters available to all departments."
        emptyCTA={{ label: 'New Template', onClick: handleCreate }}
        defaultPageSize={25}
        pageSizeOptions={[25, 50, 100]}
        panelContent={panelContent}
        panelTitle={panelTemplate?.name}
        panelSubtitle={panelTemplate ? `v${panelTemplate.version}` : undefined}
        panelDefaultWidth={420}
        onOpenFullPage={selectedItem ? () => navigate(`/inspection-templates/${selectedItem._id}`) : undefined}
        onPanelEdit={selectedItem ? () => navigate(`/inspection-templates/${selectedItem._id}`) : undefined}
        onPanelClose={() => { setSelectedItem(null); setFullTemplate(null); }}
      />

      {deleteTarget && (
        <ConfirmDeleteModal
          title="Delete Template?"
          confirmValue={deleteTarget.name}
          confirmLabel="template name"
          description={<>Are you sure you want to delete <strong>{deleteTarget.name}</strong>? This cannot be undone.</>}
          deleteButtonLabel="Delete Template"
          onConfirm={confirmDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}

      <SquirrelInterviewModal
        isOpen={squirrelModalOpen}
        onClose={() => setSquirrelModalOpen(false)}
        onComplete={handleSquirrelComplete}
        messageSender={squirrelMessageSender}
        context={{ purpose: 'library' }}
        avatarUrl="/icons/squirrel-helper.png"
        onDocumentUpload={parseDocumentForTemplate}
      />

      {createModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-[520px]" style={{ background: theme.cardBg, border: `1px solid ${theme.cardBorder}` }}>
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between" style={{ borderColor: theme.cardBorder }}>
              <div>
                <h2 className="text-base font-semibold text-gray-900" style={{ color: theme.textPrimary }}>New Inspection Template</h2>
              </div>
              <button
                onClick={() => { setCreateModalOpen(false); setNewTemplateName(''); }}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                style={{ color: theme.textMuted }}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" style={{ color: theme.textPrimary }}>Template Name <span className="text-red-500">*</span></label>
                <input
                  autoFocus
                  required
                  maxLength={250}
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && newTemplateName.trim()) handleCreateConfirm(); }}
                  placeholder="e.g. Annual Fire Code Inspection"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[var(--brand-primary)] focus:border-[var(--brand-primary)]"
                  style={{ background: theme.inputBg, borderColor: theme.inputBorder, color: theme.textPrimary }}
                />
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-gray-400" style={{ color: theme.textMuted }}>Template name is required</p>
                  <p className="text-xs text-gray-400" style={{ color: theme.textMuted }}>{newTemplateName.length}/250</p>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-2" style={{ borderColor: theme.cardBorder }}>
              <button
                onClick={() => { setCreateModalOpen(false); setNewTemplateName(''); }}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
                style={{ borderColor: theme.cardBorder, color: theme.textSecondary }}
              >
                Cancel
              </button>
              <button
                disabled={!newTemplateName.trim() || creatingTemplate}
                onClick={handleCreateConfirm}
                className={`px-4 py-2 text-sm font-medium rounded-lg text-white transition-colors ${
                  !newTemplateName.trim() || creatingTemplate
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-orange-600 hover:bg-orange-700 cursor-pointer'
                }`}
                style={!newTemplateName.trim() || creatingTemplate
                  ? { background: theme.expandedBg, color: theme.textMuted }
                  : { background: theme.accent }}
              >
                {creatingTemplate ? 'Creating…' : 'Create Template'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
